import { useCallback, useEffect, useRef, useState } from 'react';

import { wrapOutOfBoundIndex } from '../utils/array';

enum Key {
	Down = 'ArrowDown',
	Enter = 'Enter',
	Space = ' ',
	Up = 'ArrowUp',
}

interface UseSelectConfig {
	items: any[];
	isOpen?: boolean;
}

const quickId = () => Math.random().toString(16).slice(2, 8);

export const useSelect = ({ items, isOpen }: UseSelectConfig) => {
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const idRef = useRef(quickId());

	const getId = useCallback((index: number) => `${idRef.current}:${index}`, []);

	const getMenuProps = useCallback(() => {
		return {
			'aria-expanded': isOpen,
			role: 'listbox',
			...(selectedIndex !== -1 && {
				'aria-activedescendant': getId(selectedIndex),
			}),
		};
	}, [getId, isOpen, selectedIndex]);

	const getOptionProps = useCallback(
		(index: number) => {
			return {
				'aria-selected': index === selectedIndex,
				id: `${idRef.current}:${index}`,
				onFocus: () => setSelectedIndex(index),
				role: 'option',
				tabIndex: 0,
			};
		},
		[selectedIndex]
	);

	useEffect(() => {
		const onKeyPress = (e: any) => {
			const key = e.key as Key;

			if (!Object.values(Key).includes(key)) {
				return;
			}

			if ([Key.Down, Key.Up].includes(key)) {
				return setSelectedIndex((prev) => {
					const id = wrapOutOfBoundIndex(prev + (key === Key.Down ? 1 : -1), items.length);

					document.getElementById(getId(id))?.focus();

					return id;
				});
			}

			const el = document.getElementById(getId(selectedIndex));

			if (document.activeElement === el)
				return el?.dispatchEvent(
					new MouseEvent('click', {
						view: window,
						bubbles: true,
						cancelable: true,
						buttons: 1,
					})
				);
		};

		document.addEventListener('keydown', onKeyPress);

		return () => document.removeEventListener('keydown', onKeyPress);
	}, [getId, items.length, selectedIndex]);

	return { getMenuProps, getOptionProps };
};
